import type { GamingScorePersistedData } from '@playcastdotio/Messaging';
import { MAX_INSTANCE_CAPACITY } from './GameInstanceAllocator';

/**
 * Host capacity model (pool-match-lifecycle Phase 5, extended in
 * android-multi-instance-host Phase 2).
 *
 * `hostEffectiveMax = min(hostConfig.maxPlayers, reportedMaxGameInstances)`
 * where `reportedMaxGameInstances` is the machine ceiling Playjector reports
 * (InstanceCapabilitiesProvider). An unknown reported ceiling (`undefined`) is
 * a no-op, never a zero — so behavior never regresses below what ops
 * configured, and single-instance fallback is preserved. A reported ceiling of
 * `0` is a real value ("this machine cannot run any instance") and clamps to 0.
 *
 * The gaming-score hardware tier (`hardwareCap`) is NO LONGER a clamp: ops
 * `maxPlayers` plus the machine-reported ceiling are authoritative. The tier is
 * retained (`hardwareCapFromGamingScore`/`HARDWARE_TIER_CAPS`) as advisory
 * telemetry only, and the `hardwareCap` parameter is accepted-but-ignored so
 * existing callers need not change.
 */

export type HostCapacityConfig = {
  /** Ops-controlled local setting (hostCapacityConfigState). Minimum 1. */
  maxPlayers: number;
};

export type HostHardwareInfo = GamingScorePersistedData | null;

/**
 * Pinned tier → instance-cap mapping (the Phase-5 sign-off item), keyed by
 * `GamingScoreFullData.qualificationLevel`:
 *
 * - `SUPERIOR` (premium host, aggregate ≥ superior band) → 9. Preserves the
 *   pre-Phase-5 hardcoded pool size for the hardware that historically ran it.
 * - `QUALIFIED` (meets minimum requirements) → 4. Conservative mid-tier:
 *   each instance costs one MuMu VM plus one 856×480@60 encode.
 * - `DISQUALIFIED` (fails minimum requirements) → 1. Never scale beyond a
 *   single instance on hardware that fails the floor.
 */
export const HARDWARE_TIER_CAPS = {
  SUPERIOR: 9,
  QUALIFIED: 4,
  DISQUALIFIED: 1,
} as const;

/**
 * Hardware cap from the authoritative gaming score, or `null` when the score
 * carries no usable qualification tier (absent record, or an older record
 * without `fullData.qualificationLevel` — treated as "unknown", never as
 * "weak hardware").
 */
export function hardwareCapFromGamingScore(hardwareInfo: HostHardwareInfo): number | null {
  const qualificationLevel = hardwareInfo?.fullData?.qualificationLevel;
  if (!qualificationLevel) return null;
  return HARDWARE_TIER_CAPS[qualificationLevel];
}

/**
 * Effective per-host game-instance capacity:
 * `min(configMax, reportedMaxGameInstances)`, capped at `MAX_INSTANCE_CAPACITY`.
 *
 * - `configMax` — ops-controlled setting (hostCapacityConfigState). Minimum 1.
 * - `_hardwareCap` — DEPRECATED / accepted-but-ignored. The gaming-score tier
 *   no longer clamps capacity; ops `maxPlayers` + the reported ceiling are
 *   authoritative. Kept in the signature so existing callers need not change.
 * - `reportedMaxGameInstances` — Playjector-reported machine ceiling
 *   (InstanceCapabilitiesProvider); `undefined` = unknown = no-op and NEVER
 *   reduces the count (single-instance fallback is preserved). `0` is a real
 *   value — "this machine cannot run any instance" — and clamps to 0.
 */
export function resolveEffectiveMaxPlayers(
  configMax: number,
  _hardwareCap: number | undefined,
  reportedMaxGameInstances: number | undefined,
): number {
  let effective = normalizeConfigMaxPlayers(configMax);
  if (reportedMaxGameInstances !== undefined) {
    effective = Math.min(effective, reportedMaxGameInstances);
  }
  return Math.min(effective, MAX_INSTANCE_CAPACITY);
}

/**
 * Effective per-host game-instance capacity:
 * `min(config.maxPlayers, reportedMaxGameInstances)`, clamped to
 * `[0, MAX_INSTANCE_CAPACITY]`. The gaming-score hardware tier is no longer a
 * clamp (advisory telemetry only): ops `maxPlayers` plus the reported ceiling
 * are authoritative. `reportedMaxGameInstances` defaults to `undefined`
 * (unknown) so existing callers are unaffected and single-instance fallback is
 * preserved.
 */
export function effectiveMaxPlayers(
  config: HostCapacityConfig,
  _hardwareInfo: HostHardwareInfo,
  reportedMaxGameInstances?: number,
): number {
  return resolveEffectiveMaxPlayers(
    config.maxPlayers,
    undefined,
    reportedMaxGameInstances,
  );
}

/** Defensive normalization: the setting is localStorage-persisted, so garbage is possible. */
function normalizeConfigMaxPlayers(value: number): number {
  if (!Number.isInteger(value) || value < 1) return 1;
  return value;
}
